<template>
  <div class="math-toolbar">
    <v-card variant="outlined" class="pa-2">
      <div class="d-flex flex-wrap align-center ga-1">
        <!-- Formato de texto -->
        <v-btn
          size="small"
          variant="tonal"
          color="blue-grey-darken-1"
          @click="$emit('wrap', { prefix: '**', suffix: '**' })"
          title="Negrita (selecciona texto primero)"
        >
          <span style="font-weight: 900; font-size: 15px">B</span>
        </v-btn>

        <v-btn
          size="small"
          variant="tonal"
          color="blue-grey-darken-1"
          @click="$emit('wrap', { prefix: '*', suffix: '*' })"
          title="Cursiva (selecciona texto primero)"
        >
          <span style="font-style: italic; font-weight: 600; font-size: 15px">I</span>
        </v-btn>

        <v-divider vertical class="mx-1"></v-divider>

        <span class="text-caption font-weight-bold mr-2">Insertar:</span>

        <!-- Fracciones -->
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          @click="insertTemplate('fraccion')"
          title="Insertar fracción"
        >
          <span class="math-symbol">
            <sup>a</sup>⁄<sub>b</sub>
          </span>
        </v-btn>

        <!-- Potencia -->
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          @click="insertTemplate('potencia')"
          title="Insertar potencia"
        >
          <span class="math-symbol">x<sup>n</sup></span>
        </v-btn>

        <!-- Raíz cuadrada -->
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          @click="insertTemplate('raiz')"
          title="Insertar raíz cuadrada"
        >
          <span class="math-symbol">√x</span>
        </v-btn>

        <!-- Raíz n-ésima -->
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          @click="insertTemplate('raiz-n')"
          title="Insertar raíz n-ésima"
        >
          <span class="math-symbol"><sup>n</sup>√x</span>
        </v-btn>

        <!-- Subíndice -->
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          @click="insertTemplate('subindice')"
          title="Insertar subíndice"
        >
          <span class="math-symbol">x<sub>n</sub></span>
        </v-btn>

        <v-divider vertical class="mx-1"></v-divider>

        <!-- Suma -->
        <v-btn
          size="small"
          variant="tonal"
          color="secondary"
          @click="insertTemplate('suma')"
          title="Insertar sumatoria"
        >
          <span class="math-symbol">∑</span>
        </v-btn>

        <!-- Integral -->
        <v-btn
          size="small"
          variant="tonal"
          color="secondary"
          @click="insertTemplate('integral')"
          title="Insertar integral"
        >
          <span class="math-symbol">∫</span>
        </v-btn>

        <!-- Producto -->
        <v-btn
          size="small"
          variant="tonal"
          color="secondary"
          @click="insertTemplate('producto')"
          title="Insertar producto"
        >
          <span class="math-symbol">∏</span>
        </v-btn>

        <v-divider vertical class="mx-1"></v-divider>

        <!-- Símbolos comunes -->
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn size="small" variant="tonal" color="info" v-bind="props" title="Más símbolos">
              <v-icon size="small">mdi-alpha-omega</v-icon>
              <v-icon size="x-small">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="insertSymbol('pi')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">π</span>
              </template>
              <v-list-item-title>Pi (π)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('infty')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">∞</span>
              </template>
              <v-list-item-title>Infinito (∞)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('theta')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">θ</span>
              </template>
              <v-list-item-title>Theta (θ)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('alpha')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">α</span>
              </template>
              <v-list-item-title>Alpha (α)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('beta')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">β</span>
              </template>
              <v-list-item-title>Beta (β)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('leq')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">≤</span>
              </template>
              <v-list-item-title>Menor o igual (≤)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('geq')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">≥</span>
              </template>
              <v-list-item-title>Mayor o igual (≥)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('neq')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">≠</span>
              </template>
              <v-list-item-title>Diferente de (≠)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('times')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">×</span>
              </template>
              <v-list-item-title>Por (×)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('div')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">÷</span>
              </template>
              <v-list-item-title>Dividido (÷)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="insertSymbol('pm')">
              <template v-slot:prepend>
                <span class="math-symbol mr-2">±</span>
              </template>
              <v-list-item-title>Más/menos (±)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Ayuda -->
        <v-btn
          size="small"
          variant="text"
          color="grey"
          icon
          @click="showHelp = !showHelp"
          title="Ayuda"
        >
          <v-icon size="small">mdi-help-circle</v-icon>
        </v-btn>
      </div>

      <!-- Panel de ayuda colapsable -->
      <v-expand-transition>
        <div v-if="showHelp" class="mt-3 pa-3 bg-blue-grey-lighten-5 rounded">
          <div class="text-caption">
            <div class="font-weight-bold mb-2">💡 Cómo usar el editor:</div>
            <div class="mb-1">
              • <strong>B</strong> / <em>I</em>: selecciona texto y haz clic para aplicar negrita o cursiva
            </div>
            <div class="mb-1">• Haz clic en los demás botones para insertar fórmulas matemáticas</div>
            <div class="mb-1">• Reemplaza las letras (a, b, x, n) con tus valores</div>
            <div class="mb-1">
              • Puedes mezclar texto normal, formato y fórmulas sin problema
            </div>
            <div class="mb-1">
              • La vista previa te muestra cómo se verá el resultado final
            </div>
          </div>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'MathEditorToolbar',

  emits: ['insert', 'wrap'],

  data() {
    return {
      showHelp: false,
    }
  },

  methods: {
    insertTemplate(type) {
      const templates = {
        fraccion: '$\\frac{numerador}{denominador}$',
        potencia: '$x^{n}$',
        raiz: '$\\sqrt{x}$',
        'raiz-n': '$\\sqrt[n]{x}$',
        subindice: '$x_{n}$',
        suma: '$\\sum_{i=1}^{n} x_i$',
        integral: '$\\int_{a}^{b} f(x)dx$',
        producto: '$\\prod_{i=1}^{n} x_i$',
      }

      const template = templates[type]
      if (template) {
        this.$emit('insert', template)
      }
    },

    insertSymbol(symbol) {
      const symbols = {
        pi: '$\\pi$',
        infty: '$\\infty$',
        theta: '$\\theta$',
        alpha: '$\\alpha$',
        beta: '$\\beta$',
        leq: '$\\leq$',
        geq: '$\\geq$',
        neq: '$\\neq$',
        times: '$\\times$',
        div: '$\\div$',
        pm: '$\\pm$',
      }

      const symbolText = symbols[symbol]
      if (symbolText) {
        this.$emit('insert', symbolText)
      }
    },
  },
}
</script>

<style scoped>
.math-toolbar {
  width: 100%;
}

.math-symbol {
  font-size: 16px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
}

.math-symbol sup {
  font-size: 12px;
  vertical-align: super;
}

.math-symbol sub {
  font-size: 12px;
  vertical-align: sub;
}

.v-btn {
  min-width: 40px !important;
}
</style>
